/**
 * Services Hooks
 *
 * Provides hooks for service CRUD operations with TanStack Query.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './queryKeys';

// Types

export interface ServiceLaborItemTime {
  id: string;
  serviceLaborItemId: string;
  make: string;
  model: string | null;
  yearFrom: number | null;
  yearTo: number | null;
  estimatedHours: number;
  source: 'manual' | 'tecrmi' | 'haynespro';
  sourceRef: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ServiceLaborItem {
  id: string;
  serviceId: string;
  description: string;
  estimatedHours: number;
  sortOrder: number;
  /** May be left out of a work order (#3347); a required step is always copied. */
  isOptional: boolean;
  /** For an optional step: starts ticked when the work order or quote is created. */
  selectedByDefault: boolean;
  createdAt: string;
  updatedAt: string;
  vehicleTimes?: ServiceLaborItemTime[];
}

export interface ServicePartCategoryLink {
  id: string;
  serviceId: string;
  partCategoryId: string;
  partCategoryName?: string;
  isRequired: boolean;
  defaultQuantity: number;
  notes: string | null;
  createdAt: string;
}

/** Concrete catalog item on a service. Price is the current catalog price, display only (#3843). */
export interface ServicePartLink {
  id: string;
  serviceId: string;
  partId: string;
  partName: string;
  partSku: string;
  partIsActive: boolean;
  /** Catalog category of the part (#3844). Optional while an older API may still answer. */
  partCategoryId?: string;
  catalogUnitPriceCents: number;
  quantity: number;
  sortOrder: number;
  notes: string | null;
  createdAt: string;
}

/** Part bundle linked to a service; members expand only when the service is copied (#3843). */
export interface ServicePartBundleLink {
  id: string;
  serviceId: string;
  bundleId: string;
  bundleName: string;
  itemCount: number;
  sortOrder: number;
  createdAt: string;
}

export interface ServiceExtra {
  id: string;
  serviceId: string;
  description: string;
  amountCents: number;
  isTaxable: boolean;
  hasVariableQuantity: boolean;
  unit: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface ServiceSkillLink {
  id: string;
  serviceId: string;
  skillId: string;
  skillName?: string;
  minimumProficiency: string;
  isRequired: boolean;
  createdAt: string;
}

export interface ServiceLiftLink {
  id: string;
  serviceId: string;
  liftType: string;
  isRequired: boolean;
  createdAt: string;
}

export type PricingType = 'fixed' | 'component';
export type DurationType = 'fixed' | 'variable';

export interface Service {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  sortOrder: number;
  estimatedDurationHours: number | null;
  durationType: DurationType;
  durationOptions: number[] | null;
  pricingType: PricingType;
  fixedPriceCents: number | null;
  laborRateId?: string | null;
  createdAt: string;
  updatedAt: string;
  laborItems: ServiceLaborItem[];
  partCategories: ServicePartCategoryLink[];
  /** Concrete parts (#3843). Optional: existing Service fixtures outside this file omit it. */
  parts?: ServicePartLink[];
  /** Linked bundles (#3843). Optional: existing Service fixtures outside this file omit it. */
  partBundles?: ServicePartBundleLink[];
  extras: ServiceExtra[];
  skills: ServiceSkillLink[];
  lifts: ServiceLiftLink[];
  /** 90-day tenant usage when the list was fetched with sortBy=usage (#2672). */
  usageCount?: number;
}

interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    nextCursor: string | null;
    prevCursor: string | null;
    hasMore: boolean;
  };
}

interface CreateServiceData {
  name: string;
  description?: string | null;
  isActive?: boolean;
  sortOrder?: number;
  estimatedDurationHours?: number | null;
  durationType?: DurationType;
  durationOptions?: number[] | null;
  pricingType?: PricingType;
  fixedPriceCents?: number | null;
  laborRateId?: string | null;
  laborItems?: { description: string; estimatedHours?: number; sortOrder?: number; isOptional?: boolean; selectedByDefault?: boolean }[];
  partCategoryIds?: { partCategoryId: string; isRequired?: boolean; defaultQuantity?: number; notes?: string | null }[];
  extras?: { description: string; amountCents: number; isTaxable?: boolean; hasVariableQuantity?: boolean; unit?: string | null; sortOrder?: number }[];
  skillIds?: { skillId: string; minimumProficiency?: string; isRequired?: boolean }[];
  liftIds?: { liftType: string; isRequired?: boolean }[];
}

interface UpdateServiceData {
  name?: string;
  description?: string | null;
  isActive?: boolean;
  sortOrder?: number;
  estimatedDurationHours?: number | null;
  durationType?: DurationType;
  durationOptions?: number[] | null;
  pricingType?: PricingType;
  fixedPriceCents?: number | null;
  laborRateId?: string | null;
}

// ─── Query hooks ─────────────────────────────────────────────

export function useServices(filters?: { isActive?: boolean; sortBy?: string }) {
  return useQuery({
    queryKey: queryKeys.services.list(filters ?? {}),
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('limit', '100');
      if (filters?.isActive !== undefined) params.set('isActive', String(filters.isActive));
      if (filters?.sortBy !== undefined) params.set('sortBy', filters.sortBy);
      return api.get<PaginatedResponse<Service>>(`/services?${params.toString()}`);
    },
    staleTime: 60 * 1000,
    select: (data) => data.data,
  });
}

export function useService(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.services.detail(id ?? ''),
    queryFn: () => api.get<Service>(`/services/${id}`),
    enabled: !!id,
    staleTime: 30 * 1000,
  });
}

// ─── Mutation hooks ──────────────────────────────────────────

export function useCreateService() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateServiceData) => api.post<Service>('/services', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.services.all });
    },
  });
}

export function useUpdateService() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateServiceData }) =>
      api.patch<Service>(`/services/${id}`, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.services.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.services.detail(variables.id) });
    },
  });
}

export function useDeleteService() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/services/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.services.all });
    },
  });
}

// ─── Sub-resource mutation hooks ─────────────────────────────

export function useAddLaborItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ serviceId, data }: { serviceId: string; data: { description: string; estimatedHours?: number; sortOrder?: number; isOptional?: boolean; selectedByDefault?: boolean } }) =>
      api.post<ServiceLaborItem>(`/services/${serviceId}/labor-items`, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.services.detail(variables.serviceId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.services.lists() });
    },
  });
}

export function useUpdateLaborItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ serviceId, itemId, data }: { serviceId: string; itemId: string; data: Partial<{ description: string; estimatedHours: number; sortOrder: number; isOptional: boolean; selectedByDefault: boolean }> }) =>
      api.patch<ServiceLaborItem>(`/services/${serviceId}/labor-items/${itemId}`, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.services.detail(variables.serviceId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.services.lists() });
    },
  });
}

export function useAddLaborItemTime() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      serviceId,
      itemId,
      data,
    }: {
      serviceId: string;
      itemId: string;
      data: {
        make: string;
        model?: string | null;
        yearFrom?: number | null;
        yearTo?: number | null;
        estimatedHours: number;
      };
    }) => api.post<ServiceLaborItemTime>(`/services/${serviceId}/labor-items/${itemId}/times`, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.services.detail(variables.serviceId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.services.lists() });
    },
  });
}

export function useDeleteLaborItemTime() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ serviceId, itemId, timeId }: { serviceId: string; itemId: string; timeId: string }) =>
      api.delete(`/services/${serviceId}/labor-items/${itemId}/times/${timeId}`),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.services.detail(variables.serviceId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.services.lists() });
    },
  });
}

export function useDeleteLaborItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ serviceId, itemId }: { serviceId: string; itemId: string }) =>
      api.delete(`/services/${serviceId}/labor-items/${itemId}`),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.services.detail(variables.serviceId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.services.lists() });
    },
  });
}

export function useAddPartCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ serviceId, data }: { serviceId: string; data: { partCategoryId: string; isRequired?: boolean; defaultQuantity?: number; notes?: string | null } }) =>
      api.post(`/services/${serviceId}/part-categories`, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.services.detail(variables.serviceId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.services.lists() });
    },
  });
}

/** Update a linked part category's required flag, quantity or note (#3445). */
export function useUpdatePartCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      serviceId,
      linkId,
      data,
    }: {
      serviceId: string;
      linkId: string;
      data: { isRequired?: boolean; defaultQuantity?: number; notes?: string | null };
    }) => api.patch(`/services/${serviceId}/part-categories/${linkId}`, data),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.services.detail(variables.serviceId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.services.lists() });
    },
  });
}

export function useDeletePartCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ serviceId, linkId }: { serviceId: string; linkId: string }) =>
      api.delete(`/services/${serviceId}/part-categories/${linkId}`),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.services.detail(variables.serviceId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.services.lists() });
    },
  });
}

/** Link a concrete catalog part to a service (#3843). */
export function useAddServicePart() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      serviceId,
      data,
    }: {
      serviceId: string;
      data: { partId: string; quantity?: number; sortOrder?: number; notes?: string | null };
    }) => api.post<ServicePartLink>(`/services/${serviceId}/parts`, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.services.detail(variables.serviceId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.services.lists() });
    },
  });
}

/** Update quantity, sort order or notes on a service part link (#3843). */
export function useUpdateServicePart() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      serviceId,
      linkId,
      data,
    }: {
      serviceId: string;
      linkId: string;
      data: { quantity?: number; sortOrder?: number; notes?: string | null };
    }) => api.patch<ServicePartLink>(`/services/${serviceId}/parts/${linkId}`, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.services.detail(variables.serviceId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.services.lists() });
    },
  });
}

/** Remove a concrete catalog part from a service (#3843). */
export function useDeleteServicePart() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ serviceId, linkId }: { serviceId: string; linkId: string }) =>
      api.delete(`/services/${serviceId}/parts/${linkId}`),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.services.detail(variables.serviceId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.services.lists() });
    },
  });
}

/** Link a part bundle to a service (#3843). */
export function useAddServicePartBundle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      serviceId,
      data,
    }: {
      serviceId: string;
      data: { bundleId: string; sortOrder?: number };
    }) => api.post<ServicePartBundleLink>(`/services/${serviceId}/part-bundles`, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.services.detail(variables.serviceId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.services.lists() });
    },
  });
}

/** Remove a part bundle link from a service (#3843). */
export function useDeleteServicePartBundle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ serviceId, linkId }: { serviceId: string; linkId: string }) =>
      api.delete(`/services/${serviceId}/part-bundles/${linkId}`),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.services.detail(variables.serviceId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.services.lists() });
    },
  });
}

export function useAddServiceExtra() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ serviceId, data }: { serviceId: string; data: { description: string; amountCents: number; isTaxable?: boolean; hasVariableQuantity?: boolean; unit?: string | null; sortOrder?: number } }) =>
      api.post<ServiceExtra>(`/services/${serviceId}/extras`, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.services.detail(variables.serviceId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.services.lists() });
    },
  });
}

export function useUpdateServiceExtra() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ serviceId, extraId, data }: { serviceId: string; extraId: string; data: Partial<{ description: string; amountCents: number; isTaxable: boolean; hasVariableQuantity: boolean; unit: string | null; sortOrder: number }> }) =>
      api.patch<ServiceExtra>(`/services/${serviceId}/extras/${extraId}`, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.services.detail(variables.serviceId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.services.lists() });
    },
  });
}

export function useDeleteServiceExtra() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ serviceId, extraId }: { serviceId: string; extraId: string }) =>
      api.delete(`/services/${serviceId}/extras/${extraId}`),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.services.detail(variables.serviceId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.services.lists() });
    },
  });
}

export function useAddServiceSkill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ serviceId, data }: { serviceId: string; data: { skillId: string; minimumProficiency?: string; isRequired?: boolean } }) =>
      api.post(`/services/${serviceId}/skills`, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.services.detail(variables.serviceId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.services.lists() });
    },
  });
}

export function useDeleteServiceSkill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ serviceId, linkId }: { serviceId: string; linkId: string }) =>
      api.delete(`/services/${serviceId}/skills/${linkId}`),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.services.detail(variables.serviceId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.services.lists() });
    },
  });
}

export function useAddServiceLift() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ serviceId, data }: { serviceId: string; data: { liftType: string; isRequired?: boolean } }) =>
      api.post(`/services/${serviceId}/lifts`, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.services.detail(variables.serviceId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.services.lists() });
    },
  });
}

export function useDeleteServiceLift() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ serviceId, linkId }: { serviceId: string; linkId: string }) =>
      api.delete(`/services/${serviceId}/lifts/${linkId}`),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.services.detail(variables.serviceId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.services.lists() });
    },
  });
}
